<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from "vue";
import { employerAccountingApiV1 } from "#/../js/api/client";
import { JuridicalPayer as Payer } from "#/../js/api/v1_employer_accounting/models/juridical-payer";
import { CreateJuridicalPayer } from "#/../js/api/v1_employer_accounting/models/create-juridical-payer";
import { setInnMask, setPhoneMask } from "../../../js/mask";

const emit = defineEmits<{
  (e: "payerCreated", payer: Payer): void;
  (e: "close"): void;
}>();

const creating = ref(false);
const formError = ref("");
const juridicalPayerFormData = ref<CreateJuridicalPayer>({
  companyTitle: "",
  inn: "",
  juridicalAddress: "",
  contactPerson: "",
  phone: "",
  description: "",
});
const phoneInput = useTemplateRef<HTMLElement>("phoneInput");
const innInput = useTemplateRef<HTMLElement>("innInput");

const submit = async () => {
  if (creating.value === true) {
    return;
  }

  creating.value = true;
  formError.value = "";

  try {
    const payer = (
      await employerAccountingApiV1.payer.createJuridical(
        juridicalPayerFormData.value
      )
    ).data;
    emit("payerCreated", payer);
  } catch (e) {
    formError.value = "Ошибка отправки формы";
  } finally {
    creating.value = false;
  }
};

const closeForm = () => {
  emit("close");
};

onMounted(() => {
  setInnMask(innInput.value!);
  setPhoneMask(phoneInput.value!);
});
</script>

<template>
  <div class="payer-form">
    <i class="raberu-icon-close payer-form__close" @click="closeForm"></i>

    <div v-if="formError" class="alert alert-error">{{ formError }}</div>

    <form @submit.prevent="submit">
      <div class="mb-20">
        <div class="bold mb-8">Новый плательщик</div>
        <div class="payer-form__payer-type">
          <input class="form-control" type="radio" checked /> Юридическое лицо
        </div>

        <div class="payer-form__payer-type">
          <input class="form-control" type="radio" disabled /> Физическое лицо
        </div>
      </div>

      <div>
        <div class="bold mb-8">Информация о плательщике</div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">ИНН <span class="red">*</span></label>
          </div>

          <div class="payer-form__input">
            <input
              ref="innInput"
              v-model="juridicalPayerFormData.inn"
              class="form-control"
              type="text"
              required
              pattern="[0-9]{10}|[0-9]{12}"
              title="10 или 12 цифр"
            />
          </div>
        </div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">Наименование компании <span class="red">*</span></label>
          </div>

          <div class="payer-form__input">
            <input
              v-model="juridicalPayerFormData.companyTitle"
              class="form-control"
              type="text"
              required
            />
          </div>
        </div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">Юридический адрес <span class="red">*</span></label>
          </div>

          <div class="payer-form__input">
            <input
              v-model="juridicalPayerFormData.juridicalAddress"
              class="form-control"
              type="text"
              required
            />
          </div>
        </div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">Контактное лицо <span class="red">*</span></label>
          </div>

          <div class="payer-form__input">
            <input
              v-model="juridicalPayerFormData.contactPerson"
              class="form-control"
              type="text"
              required
            />
          </div>
        </div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">Телефон <span class="red">*</span></label>
          </div>

          <div class="payer-form__input">
            <input
              ref="phoneInput"
              v-model="juridicalPayerFormData.phone"
              class="form-control"
              type="text"
              required
            />
          </div>
        </div>

        <div class="payer-form__row">
          <div class="payer-form__label">
            <label for="">Примечание</label>
          </div>

          <div class="payer-form__input">
            <textarea
              v-model="juridicalPayerFormData.description"
              class="form-control"
            ></textarea>
          </div>
        </div>

        <div class="flex justify-between gap-5" style="flex-wrap: wrap">
          <div class="mb-12">
            Оплачивая услуги, вы принимаете условия договора&#8209;оферты
          </div>
          <div>
            <button class="btn px-60" type="submit" :disabled="creating">
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.payer-form {
  background-color: #ececec;
  padding: 20px 25px;
  border-radius: 20px;
  position: relative;

  .payer-form__close {
    position: absolute;
    top: 15px;
    right: 15px;
    cursor: pointer;
    font-size: 13px;
    transition: 0.2s;

    &:hover {
      opacity: 0.7;
    }
  }

  .payer-form__payer-type {
    margin-bottom: 5px;

    > input[type="radio"].form-control {
      margin: 0 5px 0 0;
      vertical-align: middle;

      &:after {
        width: 17px;
        height: 17px;
      }

      &:before {
        width: 9px;
        height: 9px;
        margin: 5px;
      }

      &:disabled {
        &:after {
          background-color: white;
          opacity: 1;
        }
      }
    }
  }

  .payer-form__row {
    display: grid;
    grid-template-columns: 1fr 2fr;

    @media (screen and max-width: 768px) {
      grid-template-columns: 2fr 3fr;
    }

    @media (screen and max-width: 576px) {
      grid-template-columns: 1fr;
    }
  }

  .payer-form__label {
  }

  .payer-form__input {
  }
}

textarea.form-control {
  min-height: 85px;
}
</style>
